import React, { useEffect, useLayoutEffect, useState } from 'react'
import Navbar from '../../components/Navbar/Navbar'
import posthog from 'posthog-js'
import { useSearchParams } from 'react-router-dom'
import { MdShare, MdContentCopy, MdCheck } from 'react-icons/md'
import {
  fetchMyReferralCodeApi, getLoginPage, getQueryVariable, hasCustomerLogin,
  Platform,
} from '../../script/util'
import StoreToken from '../../components/StoreToken'
import { useStateFields } from '../../store/hooks'
import { RedeemInfoSheet } from '@egreact/shared'
import { useReferralProgramme } from '../../script/useReferralLive'
import ReferralRewardCard from './ReferralRewardCard'
import './Referral.css'

const BRAND = Platform.isWG ? 'WedGraphia' : 'EventGraphia'

// What the share card says in place of a code the server will not lend
// out yet, by its blocked_reason. A code only works once its owner has a
// confirmed booking, so showing one before then would hand friends a
// code that is refused when they apply it.
const CODE_BLOCKED_TEXT = {
  no_confirmed_booking: `Book with ${BRAND}. Your referral code `
    + 'unlocks once your booking is confirmed.',
  planner: 'Planners are not part of the referral programme.',
  blocked: 'Your referral code is not active right now. Please contact '
    + 'us for help.',
}
const CODE_ERROR_TEXT = 'Could not load your referral code. Please try '
  + 'again later.'

// The friend's headline is the best band going, which is the one the page
// can honestly say they could save.
const topRefereePct = (bands) => (
  bands.length
    ? Math.max(...bands.map((band) => Number(band.referee_pct)))
    : null
)

const Referral = () => {

  document.title = `Referral Program | ${BRAND}`;

  const { storeToLocal } = StoreToken()

  const [shareState, setShareState] = useState('idle')
  const [copyState, setCopyState] = useState('idle')
  const [showRedeem, setShowRedeem] = useState(false)
  // The server's answer for the caller's own code: null until it lands,
  // 'expired' when it refused the token, 'error' when the call failed.
  const [myCode, setMyCode] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  // Only a trigger: the profile landing after the login handoff rerenders
  // the page, which is when the token it parked is first seen.
  const { userDetails } = useStateFields(['userDetails'])

  // Login hands the token back on the URL. This is the same handoff the
  // other pages run: park it in localStorage, strip it off the URL, then
  // load the profile the referral code is read from. Guarded on a token
  // existing because storeToLocal redirects to login when it finds none,
  // and this page has a logged out face of its own to show instead.
  useLayoutEffect(() => {
    if (getQueryVariable('token') || localStorage.eventGraphiaToken) {
      storeToLocal()
    }
  }, [])

  // Logged in by the token alone, the one thing the code lookup needs;
  // auth_dict fields such as the phone are not on every profile. A token
  // the server refuses counts as logged out, so the login prompt shows.
  const hasToken = hasCustomerLogin()
  useEffect(() => {
    if (!hasToken) return undefined
    let active = true
    fetchMyReferralCodeApi()
      .then((data) => { if (active) setMyCode(data) })
      .catch((error) => {
        if (active) {
          setMyCode(error?.response?.status === 401 ? 'expired' : 'error')
        }
      })
    return () => { active = false }
  }, [hasToken])
  const loggedIn = hasToken && myCode !== 'expired'

  const programme = useReferralProgramme()

  const bands = programme?.bands || []
  const friendPct = topRefereePct(bands)
  const referralCode = myCode?.code || null
  let codeNote = null
  if (loggedIn && !referralCode) {
    if (myCode === null) codeNote = 'Loading your referral code…'
    else if (myCode === 'error') codeNote = CODE_ERROR_TEXT
    else codeNote = CODE_BLOCKED_TEXT[myCode.blocked_reason] || CODE_ERROR_TEXT
  }

  // Where the friend lands. Tagged the same way the sidebar and wallet
  // entry points tag their links, so shared opens stay attributable.
  const referralPageUrl = `${window.location.origin}/referral`
    + '?utm_source=share&utm_campaign=referral'

  // Native share sheet where the browser has one, clipboard with the whole
  // pitch everywhere else, and the icon says which of the two happened.
  const shareCode = async () => {
    const text = 'Hey! 👋\n'
      + `If you’re planning an event, check out ${BRAND} for `
      + 'professional photography 📸✨\n'
      + `Use my referral code ${referralCode} while booking to get an `
      + 'exclusive discount. 🎁\n'
      + referralPageUrl
    try {
      if (navigator.share) {
        await navigator.share({ title: `${BRAND} referral`, text })
        return
      }
      await navigator.clipboard.writeText(text)
      setShareState('copied')
      setTimeout(() => setShareState('idle'), 2000)
    } catch (error) {
      // Share sheet dismissed, or the clipboard refused. Neither is
      // worth interrupting the page for.
    }
  }

  // The code on its own, for anyone typing it into a chat themselves.
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch (error) {
      // Clipboard refused; nothing worth interrupting the page for.
    }
  }

  // The hero's words, shown in the hero card on laptop and atop the reward
  // band on mobile; CSS hides whichever copy the layout does not use.
  const heroText = (
    <>
      <p className="ref-hero-eyebrow">✨ A little thank you from {BRAND}</p>
      <h1 className="ref-hero-title">Refer &amp; Earn<br />Big Rewards</h1>
    </>
  )

  // Same banner twice over: the left column on desktop, between the reward
  // and How It Works on mobile. Built once so the two cannot say different
  // things, and placed by the class that decides which layout shows it.
  const winWinBlock = (variantClass) => (
    <div className={`ref-winwin ${variantClass}`}>
      <span className="ref-winwin-icon">🤝</span>
      <div>
        <div className="ref-winwin-title">Your friend saves too!</div>
        <div className="ref-winwin-desc">
          {friendPct !== null
            ? `The person you refer gets up to ${friendPct}% off their booking, a win for both of you.`
            : 'The person you refer gets the same amount as a discount on their booking, a win for both of you.'}
        </div>
      </div>
    </div>
  )

  // Straight under the hero on both layouts, so the code is the first
  // thing on the page rather than the last. One copy, since the two
  // layouts no longer want it in different places.
  const shareBlock = (
    <div className="ref-share refer-sheen">
      <div className={`ref-share-copy${codeNote ? ' ref-share-copy--note' : ''}`}>
        {referralCode ? (
          <>
            <span className="ref-share-label">Your referral code</span>
            <span className="ref-share-code">{referralCode}</span>
          </>
        ) : codeNote ? (
          <>
            <span className="ref-share-label">Your referral code</span>
            <span className="ref-share-prompt">{codeNote}</span>
          </>
        ) : (
          <span className="ref-share-prompt">
            Login or sign up to share your referral code
          </span>
        )}
      </div>
      <div className="ref-share-actions">
        {referralCode ? (
          <>
            <button
              type="button"
              className="ref-share-btn"
              onClick={shareCode}
              aria-label="Share referral code"
              title="Share"
            >
              {shareState === 'copied' ? <MdCheck /> : <MdShare />}
            </button>
            <button
              type="button"
              className="ref-share-btn ref-share-btn--ghost"
              onClick={copyCode}
              aria-label="Copy referral code"
              title={copyState === 'copied' ? 'Copied!' : 'Copy'}
            >
              {copyState === 'copied' ? <MdCheck /> : <MdContentCopy />}
            </button>
          </>
        ) : codeNote ? null : (
          <button
            type="button"
            className="ref-share-btn ref-share-btn--login"
            onClick={() => getLoginPage()}
          >
            Login
          </button>
        )}
      </div>
    </div>
  )

  // A logged in visitor gets their customer_id on the URL, the same as
  // the sidebar and wallet links carry it, however they reached the page.
  // Waits for the token handoff to clear the URL so it is not kept.
  useEffect(() => {
    if (searchParams.get('customer_id') || searchParams.get('token')) {
      return
    }
    let customerId = null
    try {
      customerId = JSON.parse(localStorage.getItem('auth_dict') || '{}')?.eventGraphiaId
    } catch (e) {
      return
    }
    if (customerId) {
      const next = new URLSearchParams(searchParams)
      next.set('customer_id', customerId)
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, userDetails])

  const customerIdParam = searchParams.get('customer_id')
  useEffect(() => {
    if (customerIdParam) {
      posthog.register({ customer_id: customerIdParam })
      posthog.capture('referral_page_opened', { customer_id: customerIdParam })
    }
  }, [customerIdParam])

  return (
  <div className="ref-page">
    <Navbar title="Referral" loggedIn={loggedIn} />

    <div className="ref-layout">

      {/* LEFT COLUMN */}
      <div className="ref-left">

        {/* Hero text; laptop only, mobile shows it atop the reward band. */}
        <div className="ref-hero">{heroText}</div>

        {/* Share code */}
        {shareBlock}

        {/* Desktop copy; the mobile one sits in the right column. */}
        {winWinBlock('ref-winwin--desktop-only')}

      </div>

      {/* RIGHT COLUMN */}
      <div className="ref-right">

        <ReferralRewardCard programme={programme} hero={heroText} />

        {/* The page quotes the reward in rupees, so it owes the reader the
            same answer the wallet gives: what a coin is actually worth,
            each way it can be spent. Same sheet, so the two cannot drift. */}
        <button
          type="button"
          className="ref-redeem-info-link"
          onClick={() => setShowRedeem(true)}
        >
          How to redeem EG Coins?
        </button>

        {/* Mobile copy; the desktop one sits in the left column. */}
        {winWinBlock('ref-winwin--mobile-only')}

        {/* How it works */}
        <div className="ref-section">
          <h2 className="ref-section-title">How It Works</h2>

          <div className="ref-steps">
            <div className="ref-step">
              <div className="ref-step-track">
                <div className="ref-step-dot">1</div>
                <div className="ref-step-line" />
              </div>
              <div className="ref-step-content">
                <div className="ref-step-heading">
                  <span className="ref-step-icon">💬</span>
                  <span className="ref-step-title">Refer {BRAND}</span>
                </div>
                <div className="ref-step-desc">Share your referral code with friends, family, or colleagues.</div>
              </div>
            </div>

            <div className="ref-step">
              <div className="ref-step-track">
                <div className="ref-step-dot">2</div>
                <div className="ref-step-line" />
              </div>
              <div className="ref-step-content">
                <div className="ref-step-heading">
                  <span className="ref-step-icon">📅</span>
                  <span className="ref-step-title">Booking Confirmed</span>
                </div>
                <div className="ref-step-desc">Your friend applies your code and confirms their booking.</div>
              </div>
            </div>

            <div className="ref-step ref-step--last">
              <div className="ref-step-track">
                <div className="ref-step-dot ref-step-dot--final">🎉</div>
              </div>
              <div className="ref-step-content">
                <div className="ref-step-heading">
                  <span className="ref-step-icon">💰</span>
                  <span className="ref-step-title">Reward Credited!</span>
                </div>
                <div className="ref-step-desc">Once their advance (40%) payment is done, your EG Coins will be credited.</div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>

    <RedeemInfoSheet
      open={showRedeem}
      onClose={() => setShowRedeem(false)}
      rates={programme?.redeem_rates}
      limits={programme?.redeem_limits}
    />

  </div>
  )
}

export default Referral
